/**
 * This is duplicated from a file in pages_redux. Please update this file and the other if changes are made.
 *
 * Color Utils
 * This is a collection of utility functions to convert color values in and
 * out of standard formats and also in and out of our internal color object format.
 */

/**
 * Convert color objects into css strings.
 *
 * @example element.style.background = colorToString(color);
 *
 * @params data Object - In formats like these...
 *   { type: 'solid', solid: { r: 255, g: 255, b: 255, a: 1 } }
 *   { type: 'gradient', gradientType:'linear', gradientAngle:90, gradient: [{ r: 255, g: 255, b: 255, a: 1, stop:100 }] }
 *   { type: 'texture', texture: { versions: [{ uri: 'image.jpg' }] } }
 *
 * @returns String - CSS formatted string like...
 *   #FFFFFF
 *   rgba(255,255,255,1)
 *   linear-gradient(45deg, #1e5799 0%,#2989d8 50%,#207cca 51%,#7db9e8 100%);
 *   radial-gradient(ellipse at center, #1e5799 0%,#2989d8 50%,#207cca 51%,#7db9e8 100%);
 *   url("image.jpg")
 */
export function colorToString(data) {
  switch (data.type) {
    case 'solid':
      return rgbaToString(data.solid);
    case 'texture':
      if (
        data.texture &&
        data.texture.versions &&
        data.texture.versions[0] &&
        data.texture.versions[0].uri
      ) {
        return `url(${data.texture.versions[0].uri})`;
      }
      return '';
    case 'gradient':
      const colors = data.gradient
        .map((color, i) => {
          const stop = color.stop ? color.stop : i / (data.gradient.length - 1);
          return rgbaToString(color) + ' ' + stop + '%';
        })
        .join(', ');
      switch (data.gradientType) {
        case 'radial':
          return `radial-gradient(ellipse at center, ${colors})`;
        default:
          return `linear-gradient(${data.gradientAngle}deg, ${colors})`;
      }
  }
}

/**
 * Convert a color channel value into a hexadecimal string.
 * This method pads the values so they are correct hex strings.
 *
 * @params color Number(int2) - A RGBA color channel number 0-255
 * @returns String - "FF" or "0F"
 */
export function toHex(color) {
  const hex = color.toString(16);
  return hex.length == 1 ? '0' + hex : hex;
}

/**
 * Convert a RGBA object into a String
 *
 * @params color Object - A RGBA color object {r:255,g:255,b:255,a:1}
 * @returns String - Like #FFFFFF or if there is alpha "rgba(255,255,255,0.5)"
 */
export function rgbaToString(color) {
  if (color.a === 1) {
    return '#' + toHex(color.r) + toHex(color.g) + toHex(color.b);
  } else {
    return `rgba(${color.r},${color.g},${color.b},${color.a})`;
  }
}

/**
 * This method takes in colors (stings or objects) and converts them to color objects.
 *
 * @params value String|Object - Default to '#000', accepts colors in these formats:
 *   String Formats:
 *     #FFF
 *     #FFFFFF
 *     rgb(255,255,255)
 *     rgba(255,255,255,1)
 *     hsl(25,100%,100%)
 *     hsla(25,100%,100%,1)
 *     linear-gradient(45deg, #1e5799 0%,#2989d8 50%,#207cca 51%,#7db9e8 100%);
 *     radial-gradient(ellipse at center, #1e5799 0%,#2989d8 50%,#207cca 51%,#7db9e8 100%);
 *     url("img_tree.png")
 *   Object Formats:
 *     { type: 'solid', solid: { r: 255, g: 255, b: 255, a: 1 } }
 *     { type: 'gradient', gradientType:'linear', gradientAngle:90, gradient: [{ r: 255, g: 255, b: 255, a: 1, stop:100 }] }
 *     { type: 'texture', texture: { versions: [{ uri: 'image.jpg' }] } }
 *
 * @returns Object - In one of these formats
 *   { type: 'solid', solid: { r: 255, g: 255, b: 255, a: 1 } }
 *   { type: 'gradient', gradientType:'linear', gradientAngle:90, gradient: [{ r: 255, g: 255, b: 255, a: 1, stop:100 }] }
 *   { type: 'texture', texture: { versions: [{ uri: 'image.jpg' }] } }
 */
export function parseColor(value = '#000') {
  const result = typeof value === 'object' ? value : parseColorString(value);
  if (result.gradient) {
    result.gradient = result.gradient.sort((a, b) => a.stop - b.stop);
  }
  return result;
}

/**
 * This method takes in color stings and converts them to color objects.
 *
 * @params value String - Accepts colors in these formats:
 *   #FFF
 *   #FFFFFF
 *   rgb(255,255,255)
 *   rgba(255,255,255,1)
 *   hsl(25,100%,100%)
 *   hsla(25,100%,100%,1)
 *   linear-gradient(45deg, #1e5799 0%,#2989d8 50%,#207cca 51%,#7db9e8 100%);
 *   radial-gradient(ellipse at center, #1e5799 0%,#2989d8 50%,#207cca 51%,#7db9e8 100%);
 *   url("img_tree.png")
 *
 * @returns Object - In one of these formats
 *   { type: 'solid', solid: { r: 255, g: 255, b: 255, a: 1 } }
 *   { type: 'gradient', gradientType:'linear', gradientAngle:90, gradient: [{ r: 255, g: 255, b: 255, a: 1, stop:100 }] }
 *   { type: 'texture', texture: { versions: [{ uri: 'image.jpg' }] } }
 */
export function parseColorString(string) {
  const result = {};
  const parts = string.split('(');
  const method = parts[0];
  let args;
  if (parts[1]) {
    args = parts[1].split(')')[0];
    if (args) {
      args = args.trim().split(',');
    }
  }
  switch (method) {
    case 'rgb':
    case 'rgba':
      result.type = 'solid';
      result.solid = {};
      result.solid.r = parseInt(args[0], 10);
      result.solid.g = parseInt(args[1], 10);
      result.solid.b = parseInt(args[2], 10);
      result.solid.a = parseFloat(args[3] || 1, 10);
      break;
    case 'hsl':
    case 'hsla':
      result.type = 'solid';
      result.solid = hslToRgb(...args);
      break;
    case 'linear-gradient':
      // linear-gradient(45deg, #1e5799 0%,#2989d8 50%,#207cca 51%,#7db9e8 100%);
      result.type = 'gradient';
      result.gradientType = 'linear';
      result.gradient = [];
      result.gradientAngle = parseFloat(args.shift().split('deg').join(''), 10); // this doesn't support strings like "top bottom"
      args.forEach((arg) => {
        const argPart = arg.trim().split(' ');
        const color = parseColorString(argPart[0]).solid;
        const stop = parseInt(argPart[1].split('%').join(''), 10);
        color.stop = stop;
        result.gradient.push(color);
      });
      break;
    case 'radial-gradient':
      // radial-gradient(ellipse at center, #1e5799 0%,#2989d8 50%,#207cca 51%,#7db9e8 100%);
      result.type = 'gradient';
      result.gradientType = 'radial';
      result.gradient = [];
      args.shift(); // ignore the first arg for now
      args.forEach((arg) => {
        const argPart = arg.trim().split(' ');
        const color = parseColorString(argPart[0]);
        const stop = parseInt(argPart[1].split('%').join(''), 10);
        color.solid.stop = stop;
        result.gradient.push(color.solid);
      });
      break;
    case 'url':
      result.type = 'texture';
      result.texture = { versions: [{ uri: args[0] }], type: 'texture' };
      break;
    default:
      if (method[0] === '#') {
        result.type = 'solid';
        result.solid = parseHex(method);
      } else {
        console.error('Unknown color format', method, 'in', string);
      }
      break;
  }
  return result;
}

/**
 * Convert hue, saturation, luminance, alpha into a RGBA Object
 *
 * @params h Number - hue (0-360)
 * @params s Number - saturation (0-100)
 * @params l Number - luminance (0-100)
 * @params a Number - alpha (0-1)
 * @returns Object - {r:255,g:255,b:255,a:1}
 */
export function hslToRgb(h, s, l, a) {
  s = typeof s === 'string' ? parseInt(s.split('%').join(''), 10) / 100 : s;
  l = typeof l === 'string' ? parseInt(l.split('%').join(''), 10) / 100 : l;
  a = typeof a === 'string' ? parseInt(a, 10) : a;
  h /= 360;
  let r, g, b;
  if (s == 0) {
    r = g = b = l; // achromatic
  } else {
    function hueToRgb(p, q, t) {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hueToRgb(p, q, h + 1 / 3);
    g = hueToRgb(p, q, h);
    b = hueToRgb(p, q, h - 1 / 3);
  }

  return {
    r: (r * 255 + 0.5) << 0,
    g: (g * 255 + 0.5) << 0,
    b: (b * 255 + 0.5) << 0,
    a: a || 1,
  };
}

/**
 * Convert a hexadecimal color String into a RGBA Object
 * Hexadecimal Alpha isn't currently supported.
 *
 * @params string String - A hexadecimal color String #FFF or #FFFFFF
 * @returns Object - {r:255,g:255,b:255,a:1}
 */
export function parseHex(string) {
  const hex = string.split('#')[1];
  if (hex.length === 3) {
    return {
      r: parseInt(hex[0] + hex[0], 16),
      g: parseInt(hex[1] + hex[1], 16),
      b: parseInt(hex[2] + hex[2], 16),
      a: 1,
    };
  }
  return {
    r: parseInt(hex[0] + hex[1], 16),
    g: parseInt(hex[2] + hex[3], 16),
    b: parseInt(hex[4] + hex[5], 16),
    a: 1,
  };
}
